import { useMemo, useRef, useState } from 'react';
import {
  BUG_AREAS,
  BUG_SEVERITIES,
  MAX_SUBJECT_LENGTH,
  MIN_DESCRIPTION_LENGTH,
  MIN_SUBJECT_LENGTH,
  attachmentError,
  bugAttachmentRules,
  useCreateBugReportMutation,
} from 'app/services/bugReportApi';
import type { IBugReportCreated } from 'app/services/bugReportApi';

/**
 * "Report a Bug" popup, opened from the header's More Actions menu
 * (GlobalHeader) and therefore reachable from every SPA page.
 *
 * The form asks for five things and captures the rest itself — page URL,
 * browser, viewport, build versions, and the project/AHU id when the route has
 * one (see `collectContext` / `toFormData` in `app/services/bugReportApi.ts`).
 * That is deliberate: the fields a reporter forgets are exactly the ones triage
 * needs, and asking for them is how a form stops getting used.
 *
 * Submission is a single POST that queues the report for the central pipeline,
 * which stores it and emails support. Success shows a short reference to quote;
 * a failure (503) keeps everything the reporter typed so they can retry.
 *
 * ★ Styling: the markup carries NO inline styles and reuses the app's own form
 * chrome — `.popup-wrapper` / `.popup-content` / `.popup-heading` /
 * `.popup-content-part` / `.popup-control-buttons` for the shell, and `.label` /
 * `.required` for each row, exactly as EditProfilePopup does. `_ui.input.scss`
 * already gives inputs and textareas the underline treatment, the #2196f3 focus
 * colour and the `.required:after` asterisk, so a hand-rolled bordered field
 * would be the thing that looked foreign. The layout-only rules live in
 * `django/static/css/common/sass/ui/popup/_bug_report.scss` — edit the SCSS and
 * re-run `npx gulp common:sass`, never `common.css` directly (CLAUDE.md,
 * "Shared legacy CSS").
 *
 * Esc comes from the shared popup convention: the global body handler
 * (`usePopupKeys`) CLICKS `button[name="cancel"]` in the topmost
 * `.popup-wrapper.active`, which is why Cancel carries that name — and why
 * routing Cancel through `requestClose` covers Esc for free.
 *
 * ⚠ That handler is mounted per PAGE, and four pages that render GlobalHeader
 * never mount it: Partners, KAI Select, CMS Admin and the CAD viewer. Esc
 * therefore does nothing to this popup there. Not worth fixing here — Cancel
 * works everywhere — but it is why Esc must not be the only way out.
 */

interface BugReportPopupProps {
  onClose: () => void;
  /** The page the reporter is on — GlobalHeader's `appName`, which is also the
   *  server's `area` value, so it pre-selects the Area field. */
  appName?: string;
}

const GENERIC_ERROR = 'The report could not be sent. Please try again, or email support directly.';

/**
 * First human-readable message out of an error response.
 *
 * ★ Not every failure comes from Django. nginx answers an oversize body with an
 * HTML 413 and the ALB answers a slow request with an HTML 504, and
 * `apiConfig.ts` uses the default `json` response handler — so RTK Query hands
 * those back as `{status: 'PARSING_ERROR', originalStatus, data: '<html>…'}`.
 * Returning `data` on sight would print that page's markup into the error line
 * (React escapes it, so it is ugly rather than unsafe). Non-JSON bodies are
 * therefore turned into our own sentence, keyed off `originalStatus`.
 */
const errorMessage = (error: unknown): string => {
  const err = (error ?? {}) as { status?: number | string; originalStatus?: number; data?: unknown };
  if (err.status === 'PARSING_ERROR') {
    if (err.originalStatus === 413) {
      const { maxVideoBytes } = bugAttachmentRules();
      return `That attachment is too large to upload. The limit is ${Math.round(
        maxVideoBytes / 1048576
      )} MB for a screen recording.`;
    }
    return GENERIC_ERROR;
  }

  const { data } = err;
  if (typeof data === 'string') return data;
  if (data && typeof data === 'object') {
    const record = data as Record<string, unknown>;
    if (typeof record.error === 'string') return record.error;
    for (const value of Object.values(record)) {
      if (typeof value === 'string') return value;
      if (Array.isArray(value) && typeof value[0] === 'string') return value[0];
    }
  }
  if (err.status === 429) return 'Too many reports in the last hour. Please try again later.';
  return GENERIC_ERROR;
};

const BugReportPopup = ({ onClose, appName }: BugReportPopupProps) => {
  const [createBugReport, { isLoading }] = useCreateBugReportMutation();

  const defaultArea = useMemo(
    () => (BUG_AREAS.some((a) => a.value === appName) ? (appName as string) : 'other'),
    [appName]
  );

  const [area, setArea] = useState(defaultArea);
  const [severity, setSeverity] = useState('major');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [steps, setSteps] = useState('');
  const [attachment, setAttachment] = useState<File | null>(null);
  const [reqError, setReqError] = useState('');
  const [created, setCreated] = useState<IBugReportCreated | null>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const { maxAttachmentBytes, maxVideoBytes } = bugAttachmentRules();
  const attachmentMb = Math.round(maxAttachmentBytes / 1048576);
  const videoMb = Math.round(maxVideoBytes / 1048576);

  // Mirrors the server's own minimums so Submit is blocked before a round trip
  // (api/rest_bug_report/serializers.py).
  const canSubmit =
    subject.trim().length >= MIN_SUBJECT_LENGTH &&
    description.trim().length >= MIN_DESCRIPTION_LENGTH &&
    !isLoading;

  const hasDraft = Boolean(
    subject.trim() || description.trim() || steps.trim() || attachment
  );

  /**
   * The only way this popup closes while there is something to lose.
   *
   * Esc reaches here too (usePopupKeys clicks `button[name="cancel"]`), and Esc
   * is easy to hit by accident — it is also how you cancel an IME composition.
   * Losing a paragraph of carefully written repro steps to that is the kind of
   * thing that stops someone filing the next report.
   *
   * While the POST is in flight, closing is refused outright rather than
   * confirmed: the request is not cancelled by unmounting, so the report would
   * be filed with the reporter never seeing its reference — and they would
   * reasonably file it again.
   */
  const requestClose = () => {
    if (isLoading) return;
    if (hasDraft && !created) {
      setConfirmDiscard(true);
      return;
    }
    onClose();
  };

  const clearFileInput = () => {
    // Clearing the input as well as the state means re-picking the SAME file
    // still fires `change` — the browser compares values, not identity — which
    // matters most right after a rejection, when the obvious next move is to
    // pick it again.
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  /**
   * Validate the picked file before it is ever uploaded.
   *
   * Without this the reporter waits out the whole upload to be told no: the
   * serializer rejects anything over the cap, and nginx kills anything over its
   * `client_max_body_size` with an HTML 413 while the browser is still writing
   * the body. The server remains the authority — this is the version of the
   * answer that arrives in time to be useful.
   */
  const onPickFile = (file: File | null) => {
    if (!file) {
      setAttachment(null);
      return;
    }
    const problem = attachmentError(file);
    if (problem) {
      setAttachment(null);
      clearFileInput();
      setReqError(problem);
      return;
    }
    setReqError('');
    setAttachment(file);
  };

  const onSubmit = async () => {
    if (!canSubmit) return;
    setReqError('');
    try {
      const report = await createBugReport({
        subject,
        description,
        steps,
        severity,
        area,
        attachment,
      }).unwrap();
      setCreated(report);
    } catch (error) {
      setReqError(errorMessage(error));
    }
  };

  /**
   * Rendered OVER the form, not instead of it — the form stays mounted.
   *
   * Returning a different tree here unmounted the uncontrolled
   * `<input type="file">`, so Keep editing brought it back empty while
   * `attachment` was still in state: the control said "No file chosen" and Send
   * Report uploaded the file anyway. Keeping the form mounted means Keep editing
   * really does put the reporter back exactly where they were.
   *
   * It is the LAST `.popup-wrapper.active` in the DOM, so usePopupKeys acts on
   * this one, and Keep editing is the button named `cancel` — Esc on this step is
   * the safe answer rather than the destructive one.
   */
  const discardConfirm = (
    <div className="popup-wrapper bug-report-popup bug-report-done bug-report-discard active">
      <div className="popup-content">
        <h2 className="popup-heading">Discard this bug report?</h2>
        <div className="popup-content-part">
          <p>What you have written will be lost. Nothing has been sent to support yet.</p>
        </div>
        {/* `.popup-control-buttons button` is `float: right`, so the FIRST in the
            DOM lands rightmost — where this app puts the primary action. Keep
            editing is the primary here; discarding is the exception. */}
        <div className="popup-control-buttons">
          <button
            className="btn highlighted-btn"
            type="button"
            name="cancel"
            onClick={() => setConfirmDiscard(false)}
          >
            Keep editing
          </button>
          <button
            className="btn not-highlighted-btn"
            type="button"
            onClick={() => {
              setConfirmDiscard(false);
              onClose();
            }}
          >
            Discard
          </button>
        </div>
      </div>
    </div>
  );

  if (created) {
    return (
      <div id="popup-content" className="popup-wrapper bug-report-popup bug-report-done active">
        <div className="popup-content">
          <h2 className="popup-heading">Thanks — report sent</h2>
          <div className="popup-content-part">
            <p>
              Kinetix support has your report. If you follow up, quote reference{' '}
              <strong>{created.reference}</strong>.
            </p>
          </div>
          <div className="popup-control-buttons">
            <button
              className="btn highlighted-btn"
              type="button"
              name="cancel"
              autoFocus
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div id="popup-content" className="popup-wrapper bug-report-popup active">
        <div className="popup-content">
          <h2 className="popup-heading">Report a Bug</h2>
          <div className="popup-content-part">
            <div className="bug-field">
              <label className="label" htmlFor="bug_report_area">
                Area
              </label>
              <div className="bug-control">
                <select
                  id="bug_report_area"
                  value={area}
                  onChange={(e) => setArea(e.target.value)}
                >
                  {BUG_AREAS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="bug-field">
              <label className="label" htmlFor="bug_report_severity">
                Severity
              </label>
              <div className="bug-control">
                <select
                  id="bug_report_severity"
                  value={severity}
                  onChange={(e) => setSeverity(e.target.value)}
                >
                  {BUG_SEVERITIES.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="bug-field">
              <label className="label required" htmlFor="bug_report_subject">
                Title
              </label>
              <div className="bug-control">
                <input
                  id="bug_report_subject"
                  type="text"
                  autoFocus
                  maxLength={MAX_SUBJECT_LENGTH}
                  placeholder="Coil rating comes back as 0 CFM"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                />
              </div>
            </div>

            <div className="bug-field">
              <label className="label required" htmlFor="bug_report_description">
                What happened?
              </label>
              <div className="bug-control">
                <textarea
                  id="bug_report_description"
                  rows={4}
                  placeholder="What you expected, and what you got instead."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
            </div>

            <div className="bug-field">
              <label className="label" htmlFor="bug_report_steps">
                Steps to reproduce
              </label>
              <div className="bug-control">
                <textarea
                  id="bug_report_steps"
                  rows={3}
                  placeholder={'1. Open AHU 1234\n2. Set the cooling coil to 4 row\n3. Click Rate'}
                  value={steps}
                  onChange={(e) => setSteps(e.target.value)}
                />
              </div>
            </div>

            <div className="bug-field">
              <label className="label" htmlFor="bug_report_attachment">
                Screenshot or recording
              </label>
              <div className="bug-control bug-attachment">
                {/* `accept` is spelled out rather than `image/*` + `video/*`:
                    those wildcards let the picker offer a .svg or an .avi that
                    the server then rejects, and after a 40 MB upload is the worst
                    moment to find that out. Keep in step with
                    ALLOWED_ATTACHMENT_EXTENSIONS in
                    api/rest_bug_report/serializers.py. */}
                <input
                  id="bug_report_attachment"
                  ref={fileInputRef}
                  type="file"
                  accept=".png,.jpg,.jpeg,.gif,.webp,.pdf,.txt,.log,.csv,.json,.zip,.mp4,.mov,.webm,.m4v,.mkv"
                  onChange={(e) => onPickFile(e.target.files?.[0] ?? null)}
                />
                {/* No filename echo here — the native control already shows it
                    (common.css styles `input[type=file]` into the Browse + name
                    pair), so repeating it just said it twice. */}
                {attachment && (
                  <button
                    type="button"
                    className="bug-file-remove"
                    onClick={() => {
                      setAttachment(null);
                      clearFileInput();
                    }}
                  >
                    Remove
                  </button>
                )}
                {/* The two caps come from this server's settings, like the check
                    in onPickFile — a hint that disagrees with what the form will
                    accept is worse than no hint. */}
                <span className="bug-file-hint">
                  Optional. Image, PDF, log, CSV or zip up to {attachmentMb} MB, or a screen
                  recording (MP4, MOV, WebM, MKV) up to {videoMb} MB.
                </span>
              </div>
            </div>
          </div>
          <div className="popup-control-buttons">
            <span className="popup-note">* Required field</span>
            {reqError && <div className="req-error">{reqError}</div>}
            {/* No autoFocus here. It used to start focus on Send Report, which on
                an empty form is `non-clickable` (pointer-events:none, opacity .5)
                but not `disabled` — so a keyboard user landed on a button that
                looks dead and had to Shift+Tab back through every field. Focus
                starts on Title instead, the first thing anyone types. */}
            <button
              className={`btn highlighted-btn${canSubmit ? '' : ' non-clickable'}`}
              type="button"
              onClick={onSubmit}
            >
              {isLoading ? 'Sending…' : 'Send Report'}
            </button>
            {/* `requestClose`, not `onClose`: this is also what Esc clicks. */}
            <button
              className={`btn not-highlighted-btn${isLoading ? ' non-clickable' : ''}`}
              type="button"
              name="cancel"
              onClick={requestClose}
            >
              Cancel
            </button>
            </div>
          </div>
      </div>
      {confirmDiscard && discardConfirm}
    </>
  );
};

export default BugReportPopup;
